/* eslint-disable @typescript-eslint/no-explicit-any */
import { Flex, VStack, Text } from '@wegroup/design-system';
import React, { memo, useMemo } from 'react';
import { InsurancePolicy } from '../../../../../../advisory-report/src/types/AdvisoryReport';
import { useDeleteInsurancePolicy } from '../../../../../../parties/src/features/detail/api/insurance-policies/deleteInsurancePolicyForParty';
import { useInsurancePolicies } from '../../../../../../parties/src/features/detail/api/insurance-policies/getInsurancePoliciesByParty';
import { useUpdateInsurancePolicy } from '../../../../../../parties/src/features/detail/api/insurance-policies/putInsurancePoliciesForParty';
import { useFlowsSession } from '../../../../api/getFlowsSessionById';
import { useFlowsRouteParams } from '../../../../hooks/useFlowsRouteParams';

import PartyAddInsurancePolicyButton from '../../../../../../parties/src/features/detail/components/tabs/current-insurances/PartyAddInsurancePolicyButton';
import useCustomRiskObjectsFromPolicies from '../../../../../../advisory-report/src/features/creation-flow/components/insurance-policies/hooks/useCustomRiskObjectsFromPolicies';
import InsurancePolicyOverview from '../../../../../../advisory-report/src/features/creation-flow/components/insurance-policies/shared/InsurancePolicyOverview';
import { useTranslation } from 'react-i18next';
import { useInsurancePolicyPackages } from '../../../../../../parties/src/features/detail/api/insurance-policies/getInsurancePolicyPackagesByParty';
import PartyInsurancePolicyPackagesContainer from '../../../../../../parties/src/features/detail/components/tabs/current-insurances/PartyInsurancePolicyPackagesContainer';

const CurrentInsuranceInputComponent: React.FC<
  React.PropsWithChildren
> = () => {
  const { t } = useTranslation();

  const { sessionId } = useFlowsRouteParams();
  const session = useFlowsSession(sessionId);

  const partyId = session?.data?.prospect?.id || '';
  const policies = useInsurancePolicies({
    partyId,
  });
  const { data: policyPackages } = useInsurancePolicyPackages({ partyId });
  const filteredPackages =
    policyPackages?.items.filter(
      ({ insurance_policies }) => insurance_policies.length,
    ) || [];
  const { mutateAsync: updateOrAddPolicy } = useUpdateInsurancePolicy();
  const { mutateAsync: deletePolicy } = useDeleteInsurancePolicy();

  const insurancePolicies =
    (policies.data?.items as InsurancePolicy[] | undefined) || [];

  const _customRiskObjects = useCustomRiskObjectsFromPolicies(
    {
      current_insurances: insurancePolicies || [],
      offers: [],
    },
    partyId,
  );

  const customRiskObjects = useMemo(() => {
    return session.data?.prospect?.display_name
      ? [
          ..._customRiskObjects,
          {
            label: session.data?.prospect.display_name,
            value: session.data?.prospect.display_name,
          },
        ]
      : _customRiskObjects;
  }, [session.data?.prospect?.display_name, _customRiskObjects]);

  const handleOnRemove = (id: string, packageId?: string) => {
    if (id)
      deletePolicy({
        partyId,
        policyId: id,
        packageId,
      });
  };

  const handleOnEdit = (policy: InsurancePolicy) => {
    // edit insurance policy
    updateOrAddPolicy({ partyId, insurancePolicy: policy });
  };

  if (!insurancePolicies) return null;

  return (
    <VStack spacing="4" align="stretch" gridColumn="span 2" ml="10">
      {filteredPackages.length && (
        <PartyInsurancePolicyPackagesContainer
          partyId={partyId}
          packages={filteredPackages}
          partyType="COMPANY"
          onEditPolicy={handleOnEdit}
          onRemovePolicy={handleOnRemove}
          customRiskObjects={customRiskObjects}
        />
      )}
      <VStack align="stretch" spacing="4">
        {filteredPackages.length && (
          <VStack align="stretch" spacing="1">
            <Text textStyle="h6Headline">
              {t('ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCES_SUB-TITLE')}
            </Text>
            <Text textStyle="smallBody" color="gray.500">
              {t(
                'ADVISORY-REPORTS_CREATION-FLOW_CURRENT-INSURANCES_SUB-DESCRIPTION',
              )}
            </Text>
          </VStack>
        )}
        <InsurancePolicyOverview
          insurancePolicies={
            insurancePolicies.filter(
              ({ policy_package_id, type }) =>
                !policy_package_id && type !== 'TERMINATED',
            ) || []
          }
          controlled
          partyType="COMPANY"
          partyId={partyId}
          onEdit={handleOnEdit}
          customRiskObjects={customRiskObjects}
          onRemove={handleOnRemove}
        />
      </VStack>
      <Flex justify="flex-end">
        <PartyAddInsurancePolicyButton
          partyId={partyId}
          type="CURRENT-INSURANCE"
          insurancePolicies={insurancePolicies}
          partyDisplayName={session.data?.prospect.display_name}
          partyType={'COMPANY'}
        />
      </Flex>
    </VStack>
  );
};

export default memo(CurrentInsuranceInputComponent);
